"use client"

import styled from "@emotion/styled"
import moment from "moment"
import { useLocale } from "next-intl"
import { useEffect } from "react"
import { useInView } from "react-intersection-observer"
import colors from "theme/colors"
import {
  Container,
  Desktop,
  Mobile,
  NewText,
  transitionDelayTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { extractImage, extractProperty } from "utils"
import ReadMoreIcon from "./ReadMoreIcon"

const StyledContainer = styled.div<{
  isSmall?: boolean
  biggerBorder?: boolean
}>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  border: ${(props) =>
    props.biggerBorder
      ? "4px solid var(--DarkGreen, #5b7553)"
      : "2px solid var(--DarkGreen, #5b7553)"};
  ${(props) => props.theme.breakpoints.up("md")} {
    border: 2px solid var(--DarkGreen, #5b7553);
    grid-template-columns: ${(props) => (props.isSmall ? "1fr" : "1fr 421px")};
    height: 100%;
  }
`

const StyledImageContainer = styled.div<{ isSmall?: boolean }>`
  position: relative;
  order: -1;
  height: 262px;
  width: 100%;
  flex-shrink: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    order: ${(props) => (props.isSmall ? "-1" : "1")};
    height: ${(props) => (props.isSmall ? "300px" : "500px")};
  }
`

const StyledTextContainer = styled.div``

const StyledSmallDetails = styled.div`
  display: flex;
  align-items: center;
  font-size: 15.5px;
  gap: 8px;
`

const StyledTitleDesc = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: 8px;
`

const StyledBetween = styled.div<{ isSmall?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: 24px 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => (props.isSmall ? "24px 16px" : "48px")};
  }
`

const StyledButton = styled.div`
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  border: none;
  display: flex;
  gap: 8px;
  margin: 0px;
  padding: 0px;
  align-items: center;
  height: 20px;
  margin-top: 24px;
`

type Props = { data: any; isSmall?: boolean; biggerBorder?: boolean }

function MainNews({ data, isSmall, biggerBorder }: Props) {
  const locale = useLocale()
  const desktopImage = extractImage(data, "imageForDesktop")
  const mobileImage = extractImage(data, "imageForMobile")
  const articleDate = extractProperty(data, "articleDate")

  const formattedDate = moment(articleDate).format("MMMM D, YYYY")

  const readingTime = extractProperty(data, "readingTime")
  const wherePublished = extractProperty(data, "wherePublished")
  const title = extractProperty(data, "title")
  const shortDescription = extractProperty(data, "shortDescription")
  const linkForMore = extractProperty(data, "linkForMore")

  useEffect(() => {
    moment.locale(locale)
  }, [locale])

  const { ref, inView } = useInView(useInViewConfig)

  const content = (
    <TransitionUp $inView={inView} $delay={transitionDelayTime * 2} style={{ height: "100%" }}>
      <StyledContainer biggerBorder={biggerBorder} ref={ref} isSmall={isSmall}>
        <StyledBetween isSmall={isSmall}>
          <StyledTextContainer>
            <StyledSmallDetails>
              <NewText
                component={"span"}
                desktopFontSize={12}
                desktopLineHeight={"17px"}
                desktopFontWeight={400}
                color={colors.black}
              >
                {formattedDate}
              </NewText>{" "}
              |{" "}
              <NewText
                component={"span"}
                desktopFontSize={12}
                desktopLineHeight={"17px"}
                desktopFontWeight={400}
                color={colors.black}
              >
                {readingTime} min read
              </NewText>{" "}
              |
              <NewText
                component={"span"}
                desktopFontSize={12}
                desktopLineHeight={"17px"}
                desktopFontWeight={400}
                color={colors.black}
              >
                {wherePublished}
              </NewText>
            </StyledSmallDetails>
            <StyledTitleDesc>
              <NewText
                component={"h3"}
                desktopFontSize={32}
                desktopLineHeight={"36px"}
                desktopFontWeight={400}
                mobileFontSize={24}
                mobileLineHeight={"35px"}
                color={colors.black}
              >
                {title}
              </NewText>
              <NewText
                component={"p"}
                desktopFontSize={16}
                desktopLineHeight={"23px"}
                desktopFontWeight={400}
                mobileFontSize={14}
                mobileLineHeight={"20px"}
                color={colors.black}
              >
                {shortDescription}
              </NewText>
            </StyledTitleDesc>
          </StyledTextContainer>
          <a
            style={{ textDecorationColor: colors.darkGreen }}
            target="_blank"
            href={linkForMore?.[0]?.url || "#"}
            title={linkForMore?.[0]?.name}
          >
            <StyledButton>
              <NewText
                component={"p"}
                desktopFontSize={14}
                desktopLineHeight={"20px"}
                desktopFontWeight={700}
                mobileFontSize={12}
                mobileLineHeight={"17px"}
                color={colors.darkGreen}
              >
                {linkForMore?.[0]?.name}
              </NewText>{" "}
              <ReadMoreIcon />
            </StyledButton>
          </a>
        </StyledBetween>
        <StyledImageContainer isSmall={isSmall}>
          <Desktop>
            <ImageWithAlt image={desktopImage} fill />
          </Desktop>
          <Mobile>
            <ImageWithAlt image={mobileImage} fill />
          </Mobile>
        </StyledImageContainer>
      </StyledContainer>
    </TransitionUp>
  )

  return isSmall ? content : <Container>{content}</Container>
}

export default MainNews
